/**
 * Gift-card send-date calendar — the popup behind the calendar button on the
 * gift-card form (snippets/product-form-giftcard.liquid).
 *
 * WHY A CUSTOM ONE, AND WHY NO NATIVE DATE INPUT. The field is two inputs:
 * a visible TEXT input (data-gc-date-display) the shopper reads and, with
 * a mouse, types into, and a hidden ISO input (data-gc-date, the cart
 * property) that product.js validates. A native `<input type="date">` was
 * tried first and rejected on devices: its popup is browser-drawn and
 * unstylable, iOS WebKit summons the system picker regardless of
 * `readonly`, and every engine renders the value in its own format and on
 * its own baseline. This script owns everything about the field instead:
 * the 324 x 344 panel (month arrows left/right, today ringed, the chosen
 * day a solid NaturalC_800 disc), formatting a chosen date into the shop's
 * locale order (en-AU 04/03/2027, en-US 03/04/2027, de 04.03.2027),
 * and parsing what the shopper types (masked, digits only, on every device)
 * in that same order back to ISO. The panel opens from the calendar button
 * only. Every write to the hidden input dispatches `input` and `change`, so
 * the theme's validation runs exactly as for any other field.
 *
 * Range comes off the input's own attributes: `min` is the shop's tomorrow
 * (rendered by Liquid); there is no `max` — Givex sets no upper bound and
 * any future date must be reachable — so the calendar can never offer a
 * day the validator would reject and never withholds one it would accept.
 * Today is ringed even though it is below min — it is the orientation mark,
 * not an option.
 *
 * Locale: month and weekday names come from Intl using the page language,
 * so the six regions (and Weglot's languages) need no strings here. Week
 * starts Monday except on the US store.
 *
 * A11y: role="dialog", labelled from the markup's data attributes (locale
 * keys), Escape / outside-click / focus-loss close it, focus returns to the
 * trigger, arrow keys move between days, disabled days are real `disabled`
 * buttons. No library, no jQuery.
 */
(function () {
  'use strict';

  var WRAP_SELECTOR = '[data-gc-date-wrap]';
  var DAY_MS = 86400000;

  var ICON_PREV =
    '<svg viewBox="0 0 8 14" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M6.52344 0.284912C6.60922 0.197321 6.75016 0.197309 6.83594 0.284912L7.59375 1.05933C7.67702 1.14437 7.67702 1.27995 7.59375 1.36499L2.70996 6.35327C2.46046 6.60833 2.46045 7.01619 2.70996 7.27124L7.59375 12.2595C7.67702 12.3446 7.67702 12.4801 7.59375 12.5652L6.83594 13.3396C6.75015 13.4272 6.60922 13.4272 6.52344 13.3396L0.28125 6.96558C0.197983 6.88054 0.197982 6.74398 0.28125 6.65894L6.52344 0.284912Z" fill="currentColor" stroke="currentColor" stroke-width="0.4375"/>' +
    '</svg>';
  var ICON_NEXT =
    '<svg viewBox="0 0 9 14" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M0.343654 1.70609C0.177118 1.53601 0.177119 1.26399 0.343654 1.09391L1.10218 0.319249C1.27375 0.14403 1.55581 0.14403 1.72738 0.319249L7.96924 6.69391C8.13577 6.86399 8.13577 7.13601 7.96924 7.30609L1.72738 13.6807C1.55581 13.856 1.27375 13.856 1.10218 13.6807L0.343654 12.9061C0.177119 12.736 0.177119 12.464 0.343654 12.2939L5.22757 7.30608C5.3941 7.13601 5.3941 6.86399 5.22757 6.69391L0.343654 1.70609Z" fill="currentColor" stroke="currentColor" stroke-width="0.4375"/>' +
    '</svg>';

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  /* Bare ISO date <-> {y, m (0-based), d}. Strings, never Date.parse: a bare
     date parses as UTC and shifts the day for anyone west of Greenwich. */
  function toIso(y, m, d) { return y + '-' + pad(m + 1) + '-' + pad(d); }
  function fromIso(s) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    return match ? { y: +match[1], m: +match[2] - 1, d: +match[3] } : null;
  }
  function todayParts() {
    var now = new Date();
    return { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() };
  }
  function monthIndex(p) { return p.y * 12 + p.m; }
  function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }

  function Calendar(wrap) {
    this.wrap = wrap;
    this.input = wrap.querySelector('[data-gc-date]');
    this.display = wrap.querySelector('[data-gc-date-display]') || this.input;
    this.trigger = wrap.querySelector('[data-gc-date-open]');
    if (!this.input || !this.trigger) return;

    this.lang = this.resolveLocale();
    this.weekStart = window.theme && window.theme.store === 'US' ? 0 : 1;
    this.monthFormat = new Intl.DateTimeFormat(this.lang, { month: 'long', year: 'numeric' });
    /* Two-letter weekday initials ("Su Mo Tu We Th Fr Sa"): Intl's `short`
       form trimmed to two characters — `narrow` gives single letters, which
       the design does not use, and the trim keeps it locale-driven. */
    this.weekdayFormat = new Intl.DateTimeFormat(this.lang, { weekday: 'short' });
    this.dayLabelFormat = new Intl.DateTimeFormat(this.lang, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    /* The display format — two-digit day and month, four-digit year, in the
       LOCALE'S order and with its separator (en-AU 04/03/2027, en-US
       03/04/2027, de 04.03.2027). The same Intl parts drive typing: the
       shopper types in the order they see, so a US shopper's month-first
       entry and an AU shopper's day-first entry both parse correctly. */
    this.displayFormat = new Intl.DateTimeFormat(this.lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
    this.partOrder = ['day', 'month', 'year'];
    this.separator = '/';
    if (typeof this.displayFormat.formatToParts === 'function') {
      var parts = this.displayFormat.formatToParts(new Date(2001, 1, 3));
      var order = [];
      for (var p = 0; p < parts.length; p++) {
        if (parts[p].type === 'day' || parts[p].type === 'month' || parts[p].type === 'year') order.push(parts[p].type);
        else if (parts[p].type === 'literal' && this.separator === '/' && parts[p].value.trim()) this.separator = parts[p].value.trim();
      }
      if (order.length === 3) this.partOrder = order;
    }

    /* Typing, on EVERY device, is MASKED rather than free text: digits only
       (inputmode="numeric" brings up the number pad on phones), the locale's
       separators inserted as the shopper types (DD/MM/YYYY on an AU store,
       MM/DD/YYYY on the US store), anything else discarded. The calendar
       opens from the button only — the field itself never pops it, and being
       a plain text input it never summons a system date picker either. The
       display carries its format skeleton in `data-gc-format` so product.js
       can name it in the format error ("Enter a valid date as DD/MM/YYYY"). */
    if (this.display !== this.input) {
      this.placeholderText = this.display.getAttribute('placeholder') || '';
      this.display.setAttribute('data-gc-format', this.skeleton());
      this.display.addEventListener('input', this.onTyped.bind(this));
      this.display.addEventListener('focus', this.onDisplayFocus.bind(this));
      this.display.addEventListener('blur', this.onDisplayBlur.bind(this));
    }

    /* A value already in the hidden input (a bfcache-restored page) shows
       formatted from the start. */
    if (this.display !== this.input && this.input.value && !this.display.value) {
      this.display.value = this.formatIso(this.input.value);
    }

    this.root = document.createElement('div');
    this.root.className = 'pdp-gc-cal';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', wrap.getAttribute('data-gc-cal-label') || '');
    this.root.hidden = true;
    wrap.appendChild(this.root);

    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.trigger.setAttribute('aria-expanded', 'false');

    this.onDocumentPointer = this.onDocumentPointer.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);

    this.trigger.addEventListener('click', this.toggle.bind(this));
    this.root.addEventListener('click', this.onClick.bind(this));

    /* A text control losing focus on a phone means a software keyboard is
       closing and the viewport is about to change height. open() reads this
       timestamp to decide whether to wait for that before measuring where
       the panel landed (see settleVisibility). Capture phase on the document
       so it sees every text control on the page, not only this form's. */
    var calendar = this;
    this.lastTextBlur = 0;
    document.addEventListener('focusout', function (event) {
      if (isTextControl(event.target)) calendar.lastTextBlur = Date.now();
    }, true);
  }

  /* min always exists (the form renders the shop's tomorrow; today is the
     fallback). max is OPTIONAL: the form renders none — Givex has no upper
     bound and any future date must be reachable — so with no attribute the
     next arrow is never disabled and no future day is greyed. */
  /* The locale that decides day/month order, separator and month names.
     `<html lang>` is the source, but the stores ship it as a BARE "en" — and
     Intl reads bare "en" as en-US, i.e. month-first, which is wrong for
     five of the six stores. A bare English tag is therefore resolved by
     store: the US store is en-US (MM/DD/YYYY), every other store en-GB
     (DD/MM/YYYY). Tags that already carry a region (en-AU) or another
     language (de, fr, nl, pl…) are taken as they are — Intl orders those
     correctly on its own. */
  Calendar.prototype.resolveLocale = function () {
    var lang = (document.documentElement.lang || '').trim();
    if (!lang || /^en$/i.test(lang)) {
      return window.theme && window.theme.store === 'US' ? 'en-US' : 'en-GB';
    }
    return lang;
  };

  Calendar.prototype.range = function () {
    var min = fromIso(this.input.min) || todayParts();
    var max = fromIso(this.input.max);
    return {
      min: min,
      max: max,
      minIso: toIso(min.y, min.m, min.d),
      maxIso: max ? toIso(max.y, max.m, max.d) : null
    };
  };

  Calendar.prototype.toggle = function () {
    if (this.trigger.disabled || this.input.disabled || this.display.disabled) return;
    if (this.root.hidden) { this.open(); } else { this.close(); }
  };

  /* ISO -> the shopper-facing string, via Intl so the locale owns the
     order and separator. */
  Calendar.prototype.formatIso = function (isoValue) {
    var parts = fromIso(isoValue);
    if (!parts) return '';
    return this.displayFormat.format(new Date(parts.y, parts.m, parts.d));
  };

  /* Typed text -> ISO, or null while the entry is incomplete or not a real
     date. Digits split on any separator, read in the locale's part order;
     a two-digit year is taken as 20xx (what a shopper typing "27" means).
     Range (min) is NOT checked here — that is product.js's job, with its
     own message. */
  Calendar.prototype.parseTyped = function (text) {
    var digits = String(text || '').trim().split(/[^0-9]+/).filter(function (s) { return s.length; });
    if (digits.length !== 3) return null;
    var values = {};
    for (var i = 0; i < 3; i++) values[this.partOrder[i]] = digits[i];
    var year = values.year;
    if (year.length === 2) year = '20' + year;
    if (year.length !== 4) return null;
    var y = +year, m = +values.month, d = +values.day;
    if (m < 1 || m > 12) return null;
    if (d < 1 || d > daysInMonth(y, m - 1)) return null;
    return toIso(y, m - 1, d);
  };

  /* Digits -> the masked display string: chunks of 2 / 2 / 4 in the locale's
     part order, the locale's separator between completed chunks, stopping
     at the first incomplete chunk ("0403" -> "04/03", "040320" -> "04/03/20"). */
  Calendar.prototype.mask = function (digits) {
    var lengths = { day: 2, month: 2, year: 4 };
    var out = '';
    var index = 0;
    for (var i = 0; i < this.partOrder.length; i++) {
      var length = lengths[this.partOrder[i]];
      var chunk = digits.substr(index, length);
      if (!chunk.length) break;
      out += (out.length ? this.separator : '') + chunk;
      index += length;
      if (chunk.length < length) break;
    }
    return out;
  };

  /* The format hint shown as placeholder while the empty field has focus:
     "DD/MM/YYYY" in the locale's order and separator. */
  Calendar.prototype.skeleton = function () {
    var tokens = { day: 'DD', month: 'MM', year: 'YYYY' };
    var out = [];
    for (var i = 0; i < this.partOrder.length; i++) out.push(tokens[this.partOrder[i]]);
    return out.join(this.separator);
  };

  /* The digit string behind whatever is in the field. Two readings:
       - no separator present: the digits in order ("04032027");
       - separators present — typed by the shopper ("4/3/27"), pasted, or
         put there by the mask itself on an earlier keystroke ("04/03"):
         every separator CLOSES the chunk before it, so a one-digit day or
         month is zero-padded and the entry still lands on the right digits
         ("4/3/27" -> "040327"). The chunk after the LAST separator is still
         OPEN — it is the one being typed into — and is taken whole.
     Digits are capped at eight in either case. */
  Calendar.prototype.digitsFromText = function (raw) {
    var tokens = raw.split(/[^0-9]+/);
    if (tokens.length < 2) return raw.replace(/\D/g, '').slice(0, 8);
    var lengths = { day: 2, month: 2, year: 4 };
    var out = '';
    for (var i = 0; i < tokens.length && i < 3; i++) {
      var token = tokens[i];
      var part = this.partOrder[i];
      if (i === tokens.length - 1) {
        /* Open chunk: NEVER cut to its part's length. The mask writes
           "04/03" with no trailing separator, so the browser appends the
           next keystroke to the month chunk ("04/032"); the surplus digit
           has to flow on into the year ("04/03/2"). Cutting it here
           swallowed every digit typed after DD/MM — the field looked
           stuck. */
        out += token;
      } else {
        if (part !== 'year' && token.length === 1) token = '0' + token;
        out += token.slice(0, lengths[part] || 4);
      }
    }
    return out.slice(0, 8);
  };

  /* Typing, masked. Every keystroke (and paste) is reduced to its digits
     (see digitsFromText), re-masked and written back with the caret at the
     end. Backspacing onto a separator would otherwise delete only the
     separator, which the mask puts straight back — so when the digits did
     not change but the text got shorter, the digit just BEFORE the caret
     goes too (that is the one the shopper was aiming at). A separator the
     shopper has just typed after a complete chunk stays visible ("04/") so
     the field never appears to swallow a keystroke. The hidden ISO input is
     kept in step — ISO once the entry is a complete real date (a 2-digit
     year reads as 20xx), empty otherwise — and product.js is told (it only
     CLEARS an error on `input`; it judges on blur and at add-to-bag). */
  Calendar.prototype.onTyped = function () {
    var raw = this.display.value;
    var plainDigits = raw.replace(/\D/g, '').slice(0, 8);
    var digits;
    if (this.lastDigits && plainDigits === this.lastDigits && raw.length < (this.lastMasked || '').length) {
      /* Separator deleted, digits untouched: this is a Backspace, not typing,
         so the chunk-closing reading of the text does not apply (the
         remaining separator now sits mid-chunk and would mislead it). */
      var caret = typeof this.display.selectionStart === 'number' ? this.display.selectionStart : raw.length;
      var before = raw.slice(0, caret).replace(/\D/g, '').length;
      var at = Math.max(0, before - 1);
      digits = plainDigits.slice(0, at) + plainDigits.slice(at + 1);
    } else {
      digits = this.digitsFromText(raw);
    }
    var masked = this.mask(digits);
    /* A separator the shopper has just TYPED after a complete chunk stays
       visible ("04" + "/" -> "04/"). Recognised by the digits not having
       changed — a text that merely ENDS in a separator because a digit was
       just deleted ("04/03/2" -> Backspace -> "04/03/") must not keep it:
       the next Backspace would then land on that separator and, via the
       branch above, take the "3" with it, so the field would jump from
       "04/03/" to "04/0" instead of retreating to "04/03". */
    var separatorTyped = /[^0-9]$/.test(raw) && (this.lastDigits == null || plainDigits === this.lastDigits);
    if (masked && separatorTyped && (digits.length === 2 || digits.length === 4)) {
      masked += this.separator;
    }
    this.lastDigits = digits;
    this.lastMasked = masked;
    if (this.display.value !== masked) {
      this.display.value = masked;
      try { this.display.setSelectionRange(masked.length, masked.length); } catch (error) { /* not a text control */ }
    }
    var iso = digits.length >= 6 ? this.parseTyped(masked) : null;
    this.input.value = iso || '';
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  Calendar.prototype.onDisplayFocus = function () {
    if (!this.display.value) this.display.setAttribute('placeholder', this.skeleton());
  };

  /* Leaving the field snaps a parsed entry to the canonical display form
     ("4/3/27" -> "04/03/2027"); an incomplete entry is left as typed so the
     shopper can see what to fix while product.js reports it. The "Type
     Send Date" placeholder comes back for the empty, unfocused field. */
  Calendar.prototype.onDisplayBlur = function () {
    if (this.input.value) {
      this.display.value = this.formatIso(this.input.value);
      this.lastDigits = null;
      this.lastMasked = null;
    }
    this.display.setAttribute('placeholder', this.placeholderText || '');
  };

  /* Below the field by default; above it when the viewport has no room
     below but does above — a field low on a phone screen would otherwise
     open a panel the shopper has to scroll to find. This only picks the
     SIDE; when neither side has the full 348px (a field around the middle
     of a phone screen) the panel still overflows one edge, and
     ensureVisible() then scrolls the page so it fits. */
  Calendar.prototype.place = function () {
    var PANEL = 344 + 4;
    var rect = this.wrap.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var below = viewportHeight - rect.bottom;
    var above = rect.top;
    this.root.classList.toggle('pdp-gc-cal--above', below < PANEL && above > below);
  };

  function cssPx(name) {
    if (typeof window.getComputedStyle !== 'function') return 0;
    var value = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue(name));
    return isFinite(value) ? value : 0;
  }

  /* An element that raises a software keyboard when focused. */
  function isTextControl(node) {
    if (!node || !node.tagName) return false;
    var tag = node.tagName.toLowerCase();
    if (tag === 'textarea') return true;
    if (tag !== 'input') return false;
    var type = (node.getAttribute('type') || 'text').toLowerCase();
    return !/^(button|submit|reset|checkbox|radio|range|color|file|hidden|image)$/.test(type);
  }

  /* Scrolls the page just enough for the open panel to be fully on screen.
     place() decides above/below, but a field near the middle of a phone
     screen has less than the panel's 348px on BOTH sides, so whichever side
     it takes, one edge of the panel lands under the top or bottom of the
     viewport (or under the pinned header / the floating Add to Bag bar).
     The panel is absolutely positioned inside the field's wrap, so scrolling
     the document moves field and panel together and the fix is simply a
     scroll: the overflow past the nearer edge, with an 8px cushion.

     The visible area is the VISUAL viewport (window.visualViewport: the
     part not covered by an open keyboard, with its own offset when the
     shopper has pinch-zoomed), trimmed by what the theme pins over it —
     pdp.js publishes the header chrome's bottom edge plus a 16px gap as
     --pdp-sticky-top, and the mobile buy bar's height as
     --pdp-atb-sticky-height while `body.pdp-atb-sticky-active`.

     A page that cannot scroll far enough in the needed direction (the
     field sits near the end of the document) flips the panel to the other
     side once and re-measures, then settles for the best it can reach;
     a panel taller than the whole visible area keeps its TOP (month header
     and arrows) visible. Runs one frame after open, so the layout the
     browser measured is the one the panel is actually painted in. Smooth
     unless the shopper asked for reduced motion. */
  Calendar.prototype.ensureVisible = function (alreadyFlipped) {
    if (this.root.hidden) return;
    var vv = window.visualViewport;
    var viewTop = vv ? vv.offsetTop : 0;
    var viewHeight = vv ? vv.height : (window.innerHeight || document.documentElement.clientHeight);
    var CUSHION = 8;
    var chromeTop = cssPx('--pdp-sticky-top');
    var chromeBottom = document.body.classList.contains('pdp-atb-sticky-active') ? cssPx('--pdp-atb-sticky-height') : 0;
    var top = viewTop + (chromeTop > 0 ? chromeTop : CUSHION);
    var bottom = viewTop + viewHeight - chromeBottom - CUSHION;

    var rect = this.root.getBoundingClientRect();
    var delta = 0;
    if (rect.bottom > bottom) delta = rect.bottom - bottom;
    /* Second, so that when the panel cannot fit at all the top edge wins. */
    if (rect.top - delta < top) delta = rect.top - top;
    if (Math.abs(delta) < 1) return;

    var doc = document.documentElement;
    var scrollY = window.pageYOffset || doc.scrollTop || 0;
    var maxDown = Math.max(0, doc.scrollHeight - (scrollY + (window.innerHeight || doc.clientHeight)));
    var maxUp = Math.max(0, scrollY);
    var above = this.root.classList.contains('pdp-gc-cal--above');
    if (!alreadyFlipped) {
      if (delta > maxDown + 1 && !above) {
        this.root.classList.add('pdp-gc-cal--above');
        return this.ensureVisible(true);
      }
      if (-delta > maxUp + 1 && above) {
        this.root.classList.remove('pdp-gc-cal--above');
        return this.ensureVisible(true);
      }
    }
    if (delta > maxDown) delta = maxDown;
    if (-delta > maxUp) delta = -maxUp;
    if (Math.abs(delta) < 1) return;

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      window.scrollBy({ top: delta, left: 0, behavior: reduced ? 'auto' : 'smooth' });
    } catch (error) {
      window.scrollBy(0, delta);
    }
  };

  Calendar.prototype.open = function () {
    var selected = fromIso(this.input.value);
    var range = this.range();
    var anchor = selected || range.min;
    this.view = { y: anchor.y, m: anchor.m };
    this.render();
    this.place();
    this.root.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');

    document.addEventListener('pointerdown', this.onDocumentPointer, true);
    document.addEventListener('keydown', this.onKeydown, true);
    this.root.addEventListener('focusout', this.onFocusOut);

    var focusTarget =
      this.root.querySelector('.pdp-gc-cal__day.is-selected:not(:disabled)') ||
      this.root.querySelector('.pdp-gc-cal__day:not(:disabled)') ||
      this.root.querySelector('.pdp-gc-cal__nav:not(:disabled)');
    /* Decided BEFORE focus moves into the panel: on iOS a tap on the icon
       button does not focus the button, so the textarea or input the shopper
       was typing in is still the active element right now and the keyboard
       is still up; on Android the tap already blurred it a few milliseconds
       ago, which the focusout timestamp records. Either way the keyboard is
       about to close and the viewport to grow. */
    var active = document.activeElement;
    var keyboardClosing = isTextControl(active) || (Date.now() - this.lastTextBlur) < 1000;

    /* preventScroll: the browser would otherwise scroll just the focused day
       into view, fighting the whole-panel correction below. Engines without
       the option (old Safari) ignore the argument and scroll as before. */
    if (focusTarget) {
      try { focusTarget.focus({ preventScroll: true }); } catch (error) { focusTarget.focus(); }
    }

    this.settleVisibility(keyboardClosing);
  };

  /* Runs ensureVisible() at the right moment.

     No keyboard involved: next frame, once the panel is painted — as before.

     Keyboard closing (the shopper tapped the icon straight after typing in
     the note or the date field): the keyboard's dismiss animation is still
     running, the visual viewport is still short and, on iOS, still offset,
     so a measurement now scrolls the page by an amount that is wrong the
     moment the keyboard is gone — the panel ends up half off screen, which
     is the reported edge case. So instead the correction waits for the
     viewport to settle: the visual viewport (iOS) or the window (Android
     Chrome, whose layout viewport shrinks for the keyboard) reports a
     resize, and 150ms after the last one the single correction runs. If no
     resize ever arrives — the keyboard was already down, or the engine does
     not report it — a 700ms fallback runs it anyway. One correction per
     open, never two stacked smooth scrolls; close() cancels a pending one. */
  Calendar.prototype.settleVisibility = function (waitForViewport) {
    var self = this;
    this.cancelVisibility();

    if (!waitForViewport) {
      if (typeof window.requestAnimationFrame === 'function') {
        this.visibilityFrame = window.requestAnimationFrame(function () {
          self.visibilityFrame = null;
          self.ensureVisible(false);
        });
      } else {
        self.ensureVisible(false);
      }
      return;
    }

    var vv = window.visualViewport;
    var run = function () {
      self.cancelVisibility();
      self.ensureVisible(false);
    };
    var onResize = function () {
      clearTimeout(self.visibilityTimer);
      self.visibilityTimer = setTimeout(run, 150);
    };
    this.visibilityResize = onResize;
    if (vv) vv.addEventListener('resize', onResize);
    window.addEventListener('resize', onResize);
    this.visibilityTimer = setTimeout(run, 700);
  };

  Calendar.prototype.cancelVisibility = function () {
    if (this.visibilityTimer) { clearTimeout(this.visibilityTimer); this.visibilityTimer = null; }
    if (this.visibilityFrame && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(this.visibilityFrame);
    }
    this.visibilityFrame = null;
    if (this.visibilityResize) {
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.visibilityResize);
      window.removeEventListener('resize', this.visibilityResize);
      this.visibilityResize = null;
    }
  };

  Calendar.prototype.close = function (returnFocus) {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.cancelVisibility();
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.onDocumentPointer, true);
    document.removeEventListener('keydown', this.onKeydown, true);
    this.root.removeEventListener('focusout', this.onFocusOut);
    if (returnFocus !== false) this.trigger.focus();
  };

  Calendar.prototype.onDocumentPointer = function (event) {
    if (!this.wrap.contains(event.target)) this.close(false);
  };

  /* Focus leaving the panel (tabbing out) closes it; focus moving between
     the panel's own buttons does not. relatedTarget is null for a click on
     non-focusable page chrome, which the pointer handler already covers. */
  Calendar.prototype.onFocusOut = function (event) {
    var next = event.relatedTarget;
    if (next && !this.root.contains(next) && next !== this.trigger) this.close(false);
  };

  Calendar.prototype.onKeydown = function (event) {
    if (this.root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    var day = event.target && event.target.closest ? event.target.closest('.pdp-gc-cal__day') : null;
    if (!day) return;
    var delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
    if (!delta) return;
    event.preventDefault();
    this.moveFocus(+day.getAttribute('data-day'), delta);
  };

  /* Roving focus within the visible month; a step that leaves the month or
     lands on a disabled day is ignored rather than wrapped, so the arrows
     never silently change month under the shopper. */
  Calendar.prototype.moveFocus = function (fromDay, delta) {
    var target = fromDay + delta;
    var button = this.root.querySelector('.pdp-gc-cal__day[data-day="' + target + '"]:not(:disabled)');
    if (button) button.focus();
  };

  Calendar.prototype.onClick = function (event) {
    var nav = event.target.closest('.pdp-gc-cal__nav');
    if (nav && !nav.disabled) {
      this.shiftMonth(nav.classList.contains('pdp-gc-cal__nav--prev') ? -1 : 1);
      return;
    }
    var day = event.target.closest('.pdp-gc-cal__day');
    if (day && !day.disabled) {
      this.select(+day.getAttribute('data-day'));
    }
  };

  Calendar.prototype.shiftMonth = function (delta) {
    var index = monthIndex(this.view) + delta;
    this.view = { y: Math.floor(index / 12), m: index % 12 };
    this.render();
    var nav = this.root.querySelector(delta < 0 ? '.pdp-gc-cal__nav--prev' : '.pdp-gc-cal__nav--next');
    /* Keep focus on the arrow that was pressed unless it just became
       disabled, in which case the first pickable day takes it. */
    if (nav && !nav.disabled) { nav.focus(); }
    else {
      var first = this.root.querySelector('.pdp-gc-cal__day:not(:disabled)');
      if (first) first.focus();
    }
  };

  Calendar.prototype.select = function (day) {
    var iso = toIso(this.view.y, this.view.m, day);
    this.input.value = iso;
    if (this.display !== this.input) this.display.value = this.formatIso(iso);
    this.lastDigits = null;
    this.lastMasked = null;
    /* Same events a typed date produces, so product.js re-runs (and clears
       any standing error) exactly as for any other field. */
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
  };

  Calendar.prototype.render = function () {
    var range = this.range();
    var view = this.view;
    var viewIndex = monthIndex(view);
    var today = todayParts();
    var todayIso = toIso(today.y, today.m, today.d);
    var selectedIso = this.input.value;
    var prevDisabled = viewIndex - 1 < monthIndex(range.min);
    var nextDisabled = range.max ? viewIndex + 1 > monthIndex(range.max) : false;

    var html = '';
    html += '<div class="pdp-gc-cal__header">';
    html += '<button type="button" class="pdp-gc-cal__nav pdp-gc-cal__nav--prev" aria-label="' + this.escape(this.wrap.getAttribute('data-gc-cal-prev') || '') + '"' + (prevDisabled ? ' disabled' : '') + '>' + ICON_PREV + '</button>';
    html += '<span class="pdp-gc-cal__title" aria-live="polite">' + this.escape(this.monthFormat.format(new Date(view.y, view.m, 1))) + '</span>';
    html += '<button type="button" class="pdp-gc-cal__nav pdp-gc-cal__nav--next" aria-label="' + this.escape(this.wrap.getAttribute('data-gc-cal-next') || '') + '"' + (nextDisabled ? ' disabled' : '') + '>' + ICON_NEXT + '</button>';
    html += '</div>';

    html += '<div class="pdp-gc-cal__weekdays" aria-hidden="true">';
    for (var w = 0; w < 7; w++) {
      /* 4 Jan 1970 was a Sunday; offsetting from it yields any weekday
         without depending on the current month's layout. */
      var weekday = (this.weekStart + w) % 7;
      var initials = this.weekdayFormat.format(new Date(1970, 0, 4 + weekday)).replace(/\.$/, '').slice(0, 2);
      html += '<span class="pdp-gc-cal__weekday">' + this.escape(initials) + '</span>';
    }
    html += '</div>';

    var firstWeekday = new Date(view.y, view.m, 1).getDay();
    var lead = (firstWeekday - this.weekStart + 7) % 7;
    var count = daysInMonth(view.y, view.m);

    html += '<div class="pdp-gc-cal__grid" role="grid">';
    /* Always 6 rows x 7 cells: the panel is a fixed 344px, so the day cells
       must divide the same height every month or the rows would jump
       between 5- and 6-week months. */
    for (var cell = 0; cell < 42; cell++) {
      var dayNumber = cell - lead + 1;
      if (dayNumber < 1 || dayNumber > count) {
        html += '<span class="pdp-gc-cal__cell" aria-hidden="true"></span>';
        continue;
      }
      var iso = toIso(view.y, view.m, dayNumber);
      var disabled = iso < range.minIso || (range.maxIso !== null && iso > range.maxIso);
      var classes = 'pdp-gc-cal__day';
      if (iso === todayIso) classes += ' is-today';
      if (iso === selectedIso) classes += ' is-selected';
      html += '<span class="pdp-gc-cal__cell">';
      html += '<button type="button" class="' + classes + '" data-day="' + dayNumber + '"' +
        (disabled ? ' disabled' : '') +
        (iso === selectedIso ? ' aria-pressed="true"' : '') +
        ' aria-label="' + this.escape(this.dayLabelFormat.format(new Date(view.y, view.m, dayNumber))) + '">' +
        dayNumber + '</button>';
      html += '</span>';
    }
    html += '</div>';

    this.root.innerHTML = html;
  };

  Calendar.prototype.escape = function (value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  function init() {
    var wraps = document.querySelectorAll(WRAP_SELECTOR);
    for (var i = 0; i < wraps.length; i++) {
      if (!wraps[i].__gcCalendar) wraps[i].__gcCalendar = new Calendar(wraps[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
